import type { Coord } from '@/app/types/restaurant';

// District center coordinates for map fallback (Vietnamese names with diacritics)
export const districtCoords: Record<string, Coord> = {
  'Quận 1': { lat: 10.7769, lng: 106.7009 },
  'Quận 2': { lat: 10.7868, lng: 106.7520 },
  'Quận 3': { lat: 10.7869, lng: 106.6863 },
  'Quận 4': { lat: 10.7578, lng: 106.7013 },
  'Quận 5': { lat: 10.7569, lng: 106.6723 },
  'Quận 6': { lat: 10.7480, lng: 106.6352 },
  'Quận 7': { lat: 10.7419, lng: 106.7403 },
  'Quận 8': { lat: 10.7240, lng: 106.6283 },
  'Quận 9': { lat: 10.8400, lng: 106.7800 },
  'Quận 10': { lat: 10.7669, lng: 106.6653 },
  'Quận 11': { lat: 10.7669, lng: 106.6503 },
  'Quận 12': { lat: 10.8671, lng: 106.6413 },
  'Quận Bình Thạnh': { lat: 10.8037, lng: 106.7173 },
  'Quận Bình Tân': { lat: 10.7652, lng: 106.6040 },
  'Quận Gò Vấp': { lat: 10.8369, lng: 106.6683 },
  'Quận Phú Nhuận': { lat: 10.7969, lng: 106.6963 },
  'Quận Tân Bình': { lat: 10.7869, lng: 106.6503 },
  'Quận Tân Phú': { lat: 10.7869, lng: 106.6303 },
  'Thành phố Thủ Đức': { lat: 10.8494, lng: 106.7536 },
  'Huyện Nhà Bè': { lat: 10.6940, lng: 106.7040 },
};

// Normalize Vietnamese district names for coord lookup
export function getDistrictCoord(district: string): Coord | undefined {
  if (!district) return undefined;
  const trimmed = district.trim();
  if (districtCoords[trimmed]) return districtCoords[trimmed];
  // Fallback: strip diacritics for fuzzy match
  const normalized = trimmed
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[đĐ]/g, 'd');
  for (const [key, coord] of Object.entries(districtCoords)) {
    const keyNorm = key
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[đĐ]/g, 'd');
    if (keyNorm === normalized) return coord;
  }
  return undefined;
}

// Format price - only if not already formatted
export function formatPrice(gia: string): string {
  if (!gia) return '';
  if (gia.includes('VND')) return gia;

  let converted = gia.toLowerCase();
  converted = converted.replace(/(\d+)k/g, (_match, num) => String(Number(num) * 1000));
  converted = converted.replace(/\d+/g, (match) => {
    const num = parseInt(match);
    if (!isNaN(num)) return num.toLocaleString('vi-VN');
    return match;
  });
  return converted + ' VND';
}

// Format hours - only if not already formatted
export function formatHours(gioMo: string): string {
  if (!gioMo) return '';
  if (gioMo.match(/^\d{2}:\d{2}/)) return gioMo;

  let cleaned = gioMo.trim();
  cleaned = cleaned.replace(/30:00/g, '');
  cleaned = cleaned.replace(/(\d)h\b/g, '$1:00');
  cleaned = cleaned.replace(/\b(\d):00/g, '0$1:00');
  cleaned = cleaned.replace(/\s*-\s*/g, ' - ');
  return cleaned;
}

// Check if restaurant is open now
export function isOpenNow(gioMo: string): boolean {
  if (!gioMo) return false;
  const now = new Date();
  const hour = now.getHours();

  const newMatch = gioMo.match(/(\d{1,2}):\d{2}\s*-\s*(\d{1,2}):\d{2}/);
  if (newMatch) {
    const start = parseInt(newMatch[1]);
    const end = parseInt(newMatch[2]);
    if (end < start) return hour >= start || hour < end;
    return hour >= start && hour < end;
  }

  const matches = gioMo.match(/(\d{1,2})h?-(\d{1,2})h?/);
  if (!matches) return false;
  const start = parseInt(matches[1]);
  const end = parseInt(matches[2]);
  if (end < start) return hour >= start || hour < end;
  return hour >= start && hour < end;
}

// Fallback label for empty fields
export function safeLabel(value: string | undefined | null): string {
  return value?.trim() || 'Khac';
}
